'use client';

import { useRef, useState } from 'react';
import { Swiper as SwiperRoot, SwiperSlide } from 'swiper/react';
import type { Swiper as SwiperType } from 'swiper';
import 'swiper/css';
import Icon from '@/components/icon/Icon';
import type { PieChartData } from '@/sections/visual-strategy/types';
import {
  allocationLabel,
  DONUT_COLORS,
  formatValue,
  getConicGradient,
} from '@/sections/visual-strategy/visualStrategyUtils';

type VisualStrategyPieCarouselProps = {
  pies: PieChartData[];
};

const VisualStrategyPieCarousel = ({
  pies,
}: VisualStrategyPieCarouselProps) => {
  const [activePieIndex, setActivePieIndex] = useState(0);
  const pieSwiperRef = useRef<SwiperType | null>(null);

  const activePie = pies[activePieIndex] || pies[0];

  if (!pies.length) {
    return null;
  }

  return (
    <div className="w-full">
      <SwiperRoot
        slidesPerView={1}
        spaceBetween={12}
        loop={pies.length > 1}
        onSwiper={(swiper) => {
          pieSwiperRef.current = swiper;
        }}
        onSlideChange={(swiper) => setActivePieIndex(swiper.realIndex)}
        allowTouchMove={pies.length > 1}
      >
        {pies.map((pie) => (
          <SwiperSlide key={pie.id} className="!h-auto">
            <article>
              <h4 className="text-base font-medium text-white">
                {pie.headline}
              </h4>
              <div className="mt-4 flex items-center gap-4">
                <div
                  className="relative h-28 w-28 shrink-0 rounded-full"
                  style={{
                    background: getConicGradient(pie.allocations),
                  }}
                >
                  <div className="absolute inset-[22%] rounded-full bg-brand-800" />
                </div>
                <ul className="w-full space-y-2 text-xs">
                  {pie.allocations.map((allocation, idx) => (
                    <li
                      key={`${pie.id}-${allocationLabel(allocation)}-${idx}`}
                      className="flex items-center justify-between gap-3 text-white"
                    >
                      <span className="inline-flex items-center gap-2 text-white">
                        <span
                          className="h-2.5 w-2.5 rounded-sm"
                          style={{
                            backgroundColor:
                              DONUT_COLORS[idx % DONUT_COLORS.length],
                          }}
                        />
                        {allocationLabel(allocation)}
                      </span>
                      <strong>{`${formatValue(allocation.percentage)}%`}</strong>
                    </li>
                  ))}
                </ul>
              </div>
            </article>
          </SwiperSlide>
        ))}
      </SwiperRoot>

      {pies.length > 1 && (
        <div className="mt-4 flex items-center justify-center gap-5 text-sm text-white">
          <button
            type="button"
            aria-label="Vorheriges Kreisdiagramm"
            onClick={() => pieSwiperRef.current?.slidePrev()}
            className="inline-flex h-7 w-7 items-center justify-center rounded-full text-white"
          >
            <Icon name="chevron-left" size={16} />
          </button>
          <span>{activePie?.title}</span>
          <button
            type="button"
            aria-label="Nächstes Kreisdiagramm"
            onClick={() => pieSwiperRef.current?.slideNext()}
            className="inline-flex h-7 w-7 items-center justify-center rounded-full text-white"
          >
            <Icon name="chevron-right" size={16} />
          </button>
        </div>
      )}
    </div>
  );
};

export default VisualStrategyPieCarousel;
